import { Calendar, ChevronDown, ChevronRight, FileText, FolderOpen, Globe, Mail } from "lucide-react";
import { useState } from "react";
import type { SourceMetadata } from "@/types";

interface SourcesPanelProps {
  sources: SourceMetadata[];
}

/** Icon and color config per source type. */
const SOURCE_STYLES: Record<string, { icon: typeof FileText; color: string; bg: string; border: string }> = {
  rag: { icon: FileText, color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/20" },
  web: { icon: Globe, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  sharepoint: { icon: FolderOpen, color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  email: { icon: Mail, color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20" },
  calendar: { icon: Calendar, color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/20" },
};

function getSourceStyle(type: string) {
  return SOURCE_STYLES[type] ?? SOURCE_STYLES.rag;
}

/**
 * Collapsible panel displaying the sources consulted for a message.
 * Renders below the message content, above the action buttons.
 */
export function SourcesPanel({ sources }: Readonly<SourcesPanelProps>) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) return null;

  return (
    <div className="mt-3 rounded-lg border border-border-secondary bg-surface-secondary">
      <button
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-content-tertiary hover:text-content-secondary transition cursor-pointer"
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="font-medium">
          {sources.length} source{sources.length === 1 ? "" : "s"} consulted
        </span>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-1.5">
          {sources.map((src, i) => {
            const style = getSourceStyle(src.type);
            const Icon = style.icon;

            return (
              <div
                key={`${src.type}-${src.name}-${i}`}
                className={`flex items-start gap-2 px-2.5 py-1.5 rounded-lg ${style.bg} border ${style.border}`}
              >
                <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${style.color}`} />
                <div className="min-w-0 flex-1">
                  {src.url ? (
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-xs font-medium ${style.color} hover:underline truncate block`}
                      title={src.name}
                    >
                      {src.name}
                    </a>
                  ) : (
                    <span className="text-xs font-medium text-content-secondary truncate block" title={src.name}>
                      {src.name}
                    </span>
                  )}
                  {src.snippet && <p className="text-xs text-content-quaternary mt-0.5 line-clamp-2">{src.snippet}</p>}
                  <div className="flex items-center gap-2 mt-0.5">
                    {src.from && (
                      <span className="text-[10px] text-content-quaternary truncate" title={src.from}>
                        from: {src.from}
                      </span>
                    )}
                    {src.received && (
                      <span className="text-[10px] text-content-quaternary">
                        {new Date(src.received).toLocaleDateString()}
                      </span>
                    )}
                    {src.score != null && (
                      <span className="text-[10px] text-content-quaternary">
                        relevance: {Math.round(src.score * 100)}%
                      </span>
                    )}
                    {src.chunks != null && src.chunks > 0 && (
                      <span className="text-[10px] text-content-quaternary">
                        {src.chunks} chunk{src.chunks === 1 ? "" : "s"}
                      </span>
                    )}
                    {src.collections && src.collections.length > 0 && (
                      <span className="text-[10px] text-content-quaternary truncate" title={src.collections.join(", ")}>
                        {src.collections.join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
